import React, { useEffect, useRef } from 'react';
import './ConfirmDeleteModal.css';

/**
 * Shared delete confirmation modal (matches system admin Confirm Delete UI).
 */
const ConfirmDeleteModal = ({
  show,
  title = 'Confirm Delete',
  message,
  warning = 'This action cannot be undone.',
  confirmLabel = 'Delete',
  secondaryConfirmLabel = null,
  cancelLabel = 'Cancel',
  busyLabel = 'Deleting...',
  isDeleting = false,
  className = '',
  onConfirm,
  onSecondaryConfirm,
  onCancel,
}) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isDeleting) {
        onCancel?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    confirmRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [show, isDeleting, onCancel]);

  if (!show) return null;

  return (
    <div
      className={`confirm-delete-overlay ${className}`.trim()}
      role="presentation"
      onClick={() => {
        if (!isDeleting) onCancel?.();
      }}
    >
      <div
        className="confirm-delete-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="confirm-delete-header">
          <h5 id="confirm-delete-title" className="confirm-delete-title">
            {title}
          </h5>
          <button
            type="button"
            className="confirm-delete-close"
            onClick={onCancel}
            disabled={isDeleting}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="confirm-delete-body">
          {typeof message === 'string' ? <p>{message}</p> : message}
          {warning ? <p className="confirm-delete-warning">{warning}</p> : null}
        </div>
        <div className="confirm-delete-footer">
          <button
            type="button"
            className="confirm-delete-btn-cancel"
            onClick={onCancel}
            disabled={isDeleting}
          >
            {cancelLabel}
          </button>
          {secondaryConfirmLabel && onSecondaryConfirm && (
            <button
              type="button"
              className="confirm-delete-btn-secondary-delete"
              onClick={onSecondaryConfirm}
              disabled={isDeleting}
            >
              {secondaryConfirmLabel}
            </button>
          )}
          <button
            type="button"
            className="confirm-delete-btn-delete"
            onClick={onConfirm}
            disabled={isDeleting}
            ref={confirmRef}
          >
            {isDeleting ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteModal;
